import * as React from 'react';

import { DrawerContext } from '@notino/react-styleguide';
import { Video } from '@notino/shared/definitions/types';

import { getVideoIframe } from '@containers/ProductDetailContainer/utils';
import { IImageProperties } from '@helpers/utils';
import { useArrows } from '@hooks/useArrows';
import { useIsMountedRef } from '@hooks/useIsMounted';
import { isRenderingOnServer } from '@utils/isRenderingOnServer';

import { ModiFaceOverlay } from '../ModiFaceOverlay';

import { LoadableImageGalleryDrawer } from './ImageGalleryDrawer/loadable';
import { ProgressBar } from './ProgressBar';
import { Slide } from './Slide';
import {
  OuterWrapper,
  PlayerWrapper,
  SliderWrapper,
  StyledArrow,
} from './styled';
import { useTrackSwiping } from './useTrackSwiping';

export interface IImageSliderProps {
  imagesAttrs: IImageProperties[];
  drawerImagesAttrs?: IImageProperties[];
  drawerThumbImagesAttrs?: IImageProperties[];
  videos?: Video[];
  hasModiface?: boolean;
  openModifaceModal?: () => void;
}

export const ImageSlider = ({
  imagesAttrs,
  drawerImagesAttrs,
  drawerThumbImagesAttrs,
  videos = [],
  hasModiface,
  openModifaceModal,
}: IImageSliderProps) => {
  const { trackSwiping } = useTrackSwiping();
  const isMounted = useIsMountedRef();
  const { openDrawer } = DrawerContext.useDrawerContext();
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const { scrollTo, containerRef, showLeftArrow, showRightArrow } = useArrows(
    [],
    {
      scrollAmount: isRenderingOnServer() ? 0 : window.innerWidth,
      showArrowCalulationOffset: 150,
    }
  );

  const items: Array<{ id: string; renderItem: JSX.Element }> = React.useMemo(
    () => [
      ...imagesAttrs
        .map((imgProps, index) => {
          const showModifaceOverlay = hasModiface && index === 0;
          const isLastSlide =
            videos.length === 0 && index === imagesAttrs.length - 1;

          // if modiface overlay on mobile, clone main image so customer can swipe to image and view it without overlay
          if (showModifaceOverlay) {
            return [
              {
                id: `${imgProps.src}-modiface`,
                renderItem: (
                  <Slide isFirstSlide={true} imgProps={imgProps}>
                    <ModiFaceOverlay
                      onClick={(e) => {
                        e.stopPropagation();
                        openModifaceModal!();
                      }}
                    />
                  </Slide>
                ),
              },
              {
                id: imgProps.src,
                renderItem: (
                  <Slide imgProps={imgProps} isLastSlide={isLastSlide} />
                ),
              },
            ];
          }

          return {
            id: imgProps.src,
            renderItem: (
              <Slide
                imgProps={imgProps}
                isFirstSlide={index === 0}
                isLastSlide={isLastSlide}
              />
            ),
          };
        })
        .flat(),

      ...videos.map((video) => ({
        id: video.id,
        renderItem: (
          <PlayerWrapper
            dangerouslySetInnerHTML={{
              __html: getVideoIframe(video),
            }}
          />
        ),
      })),
    ],
    [hasModiface, imagesAttrs, openModifaceModal, videos]
  );

  const calculateCurrentIndex = (el: HTMLDivElement) =>
    setCurrentIndex(
      Math.floor((el.scrollLeft + window.innerWidth / 2) / window.innerWidth)
    );

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    trackSwiping('swipe');
    calculateCurrentIndex(e.currentTarget);
  };

  React.useEffect(() => {
    if (containerRef.current) {
      calculateCurrentIndex(containerRef.current);
    }
  }, [containerRef]);

  const getDrawerInitialIndex = () => {
    if (hasModiface) {
      // If currentIndex is 0, it's the Modiface overlay, so open drawer at 0 (first real image)
      if (currentIndex === 0) return 0;
      // If currentIndex is 1, it's the real first image, so open drawer at 0
      if (currentIndex === 1) return 0;
      // For other images, subtract 1 to get the correct image index
      if (currentIndex > 1) return currentIndex - 1;
    }
    return currentIndex;
  };

  const handleImageClick = () => {
    openDrawer(
      <LoadableImageGalleryDrawer
        imagesAttrs={drawerImagesAttrs!}
        thumbImagesAttrs={drawerThumbImagesAttrs}
        videos={videos}
        initialIndex={getDrawerInitialIndex()}
      />,
      {
        initialPosition: 'top',
        isTitleBorderHidden: true,
        hideScrollbar: true,
        showFade: false,
        disableDrawerExpand: true,
        title: <div style={{ width: 52 }} />,
      }
    );
  };

  return (
    <OuterWrapper>
      <StyledArrow
        direction="left"
        visible={showLeftArrow}
        onClick={() => {
          scrollTo('left');
          trackSwiping('button');
        }}
      />

      <SliderWrapper
        ref={containerRef as React.RefObject<HTMLDivElement>}
        onScroll={handleScroll}
        onClick={handleImageClick}
      >
        {items.map(({ id, renderItem }) => (
          <div key={id}>{renderItem}</div>
        ))}
      </SliderWrapper>

      {isMounted && <ProgressBar count={items.length} index={currentIndex} />}

      <StyledArrow
        direction="right"
        data-testid="chevron-right"
        visible={showRightArrow}
        onClick={() => {
          scrollTo('right');
          trackSwiping('button');
        }}
      />
    </OuterWrapper>
  );
};
